import Dexie from 'dexie';
export const db = new Dexie('adtech_inspector');

// v2 (logs + requests persistence)
db.version(2).stores({
  sessions: '++id, createdAt, title',
  bids: '++id, sessionId, adUnitCode, bidder, cpm, ts',
  timelines: '++id, sessionId, adUnitCode, ts, type',
  logs: '++id, ts, level, tag',
  requests: '++id, ts, requestId, adUnit, type'
});

// v3 — full request/response bodies keyed by requestId. In-memory retention
// keeps full bodies only for the newest FULL_BODY_KEEP rows/device (and the
// localStorage slim backup drops them entirely), so older/reloaded rows show
// "trimmed to save memory". This table stashes the full bytes to IndexedDB
// (which has room for large GAM creatives, unlike localStorage's ~5MB) so the
// detail pane can load a trimmed row's body on demand — even after a reload.
db.version(3).stores({
  bodies: 'requestId, ts'   // requestId (primary), ts for age-based eviction
});

const BODIES_MAX = 5000; // cap the body store; evict oldest beyond this

/**
 * Stash full bodies for a batch of rows. Idempotent (put by requestId), so
 * re-stashing the same row is cheap. Only writes rows that actually carry a
 * body. Fire-and-forget from the caller; never throws.
 */
export async function stashBodies(rows = []) {
  try {
    const recs = [];
    for (const r of rows) {
      const requestId = r?.requestId || r?.id;
      if (!requestId) continue;
      const hasReq = r.requestBody != null && r.requestBody !== '';
      const hasResp = r.responseBody != null && r.responseBody !== '';
      if (!hasReq && !hasResp && r.requestBodyParsed == null) continue;
      recs.push({
        requestId: String(requestId),
        ts: r.ts || r.startedAt || r.timestamp || Date.now(),
        requestBody: hasReq ? r.requestBody : undefined,
        responseBody: hasResp ? r.responseBody : undefined,
        requestBodyParsed: r.requestBodyParsed ?? undefined,
      });
    }
    if (!recs.length) return;
    await db.bodies.bulkPut(recs);

    // Evict oldest beyond the cap (cheap: only runs when over budget).
    const count = await db.bodies.count();
    if (count > BODIES_MAX) {
      const overflow = count - BODIES_MAX;
      const oldest = await db.bodies.orderBy('ts').limit(overflow).primaryKeys();
      if (oldest.length) await db.bodies.bulkDelete(oldest);
    }
  } catch { /* body-stash is best-effort — never disrupt capture */ }
}

/** Load a single row's stashed bodies by requestId. Returns null if absent. */
export async function loadBody(requestId) {
  if (!requestId) return null;
  try { return (await db.bodies.get(String(requestId))) || null; }
  catch { return null; }
}

export async function clearBodies() { try { await db.bodies.clear(); } catch { /* noop */ } }

export async function persistLogs(items = []) {
  if (!items?.length) return;
  const rows = items.map(it => ({
    ts: it.ts ?? Date.now(),
    level: it.level ?? 'info',
    tag: it.tag ?? '',
    message: it.message ?? (typeof it === 'string' ? it : JSON.stringify(it)),
  }));
  await db.logs.bulkAdd(rows);
}

export async function persistRequests(entries = []) {
  if (!entries?.length) return;
  const rows = entries.map(e => ({
    ts: e.ts ?? Date.now(),
    requestId: e.requestId || e.id || `${Date.now()}-${Math.random().toString(36).slice(2,8)}`,
    adUnit: e.adUnit || e.iu || e.slot || 'unknown',
    type: e.type || e.requestType || '',
  }));
  await db.requests.bulkAdd(rows);
}

export async function loadLogs(limit = 10000) {
  return db.logs.orderBy('id').reverse().limit(limit).toArray();
}
export async function loadRequests(limit = 50000) {
  return db.requests.orderBy('id').reverse().limit(limit).toArray();
}
export async function clearLogs() { await db.logs.clear(); }
export async function clearRequests() { await db.requests.clear(); }
